import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  FormControl,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Button,
  Typography,
  TextField
} from "@mui/material";
import { CreateNewFolder as CreateNewFolderIcon } from "@mui/icons-material";
import ArrowCircleUpIcon from "@mui/icons-material/ArrowCircleUp";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  DataGrid,
  type GridColDef,
  type GridPaginationModel,
  type GridRowId,
  type GridRowSelectionModel
} from "@mui/x-data-grid";

type AuthorizedFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export type HostMediaSelection = {
  fullPath: string;
  relPath: string;
  drive: string;
};

type HostMediaEntry = {
  name: string;
  isDir: boolean;
  size?: number;
  modTime?: string;
  relPath: string;
  fullPath: string;
};

type HostMediaResponse = {
  path: string;
  entries: HostMediaEntry[];
};

type HostMediaDrivesResponse = {
  enabled: boolean;
  drives?: string[];
  selected?: string;
};

type HostMediaGridRow = HostMediaEntry & {
  id: string;
};

type HostMediaFolderProps = {
  open: boolean;
  onClose: () => void;
  onSelect: (selection: HostMediaSelection) => void;
  authorizedFetch: AuthorizedFetch;
  initialSelectedPath?: string;
  allowedExtensions?: string[];
};

const AUTH_STORAGE_KEY = "sky-view.auth.session";
const HOST_MEDIA_LOCATION_KEY = "sky-view.host-media.location";
const HOST_MEDIA_PAGE_SIZE_OPTIONS = [25, 50, 100];

const getParentPath = (path: string): string => {
  const trimmed = path.replace(/\/+$/, "");
  if (!trimmed) {
    return "";
  }
  const parts = trimmed.split("/");
  if (parts.length <= 1) {
    return "";
  }
  return parts.slice(0, -1).join("/");
};

const resolveApiPath = (value: string): string => {
  const ensureLeadingSlash = (input: string) => (input.startsWith("/") ? input : `/${input}`);
  const ensureTrailingSlash = (input: string) => (input.endsWith("/") ? input : `${input}/`);
  const normalizeBase = (input: string) => {
    const trimmed = input.trim();
    if (!trimmed || trimmed === ".") {
      return "/";
    }
    return ensureTrailingSlash(ensureLeadingSlash(trimmed));
  };

  const fallbackBase = normalizeBase(import.meta.env.BASE_URL ?? "/");
  if (typeof window === "undefined") {
    const normalized = value.trim().replace(/^\/+/u, "");
    return fallbackBase === "/" ? `/${normalized}` : `${fallbackBase}${normalized}`;
  }

  const { pathname, href } = window.location;
  let apiBase = fallbackBase;

  if (pathname.startsWith("/webrtc/")) {
    apiBase = "/webrtc/";
  } else {
    const hostPrefixMatch = pathname.match(/^\/([^/]+:[0-9]+|[^/]+\.[^/]+)(?:\/|$)/u);
    if (hostPrefixMatch?.[1]) {
      apiBase = ensureTrailingSlash(`/${hostPrefixMatch[1]}`);
    } else {
      const directoryBase = normalizeBase(new URL(".", href).pathname);
      apiBase = directoryBase || fallbackBase;
    }
  }

  const normalized = value.trim().replace(/^\/+/u, "");
  if (apiBase === "/") {
    return `/${normalized}`;
  }
  return `${apiBase}${normalized}`;
};

const getAccessToken = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const readToken = (storage: Storage): string | null => {
    const raw = storage.getItem(AUTH_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw) as { tokens?: { accessToken?: unknown } };
      return typeof parsed?.tokens?.accessToken === "string" ? parsed.tokens.accessToken : null;
    } catch {
      return null;
    }
  };

  return readToken(window.localStorage) ?? readToken(window.sessionStorage);
};

const normalizeSelectionIds = (ids?: Set<unknown> | Iterable<unknown>): Set<GridRowId> => {
  if (!ids) {
    return new Set<GridRowId>();
  }
  const sourceIds = ids instanceof Set ? ids : new Set(ids);
  const normalizedIds = new Set<GridRowId>();
  for (const id of sourceIds) {
    if (typeof id === "string" || typeof id === "number") {
      normalizedIds.add(id);
    }
  }
  return normalizedIds;
};

const normalizeRelativePath = (value: string): string =>
  value
    .replace(/\\/g, "/")
    .replace(/^\/+/u, "")
    .replace(/\/+$/u, "");

const parseHostMediaLocation = (input?: string): { drive: string; path: string } => {
  const raw = (input ?? "").trim();
  if (!raw) {
    return { drive: "", path: "" };
  }

  let value = raw;
  if (value.toLowerCase().startsWith("file://")) {
    value = value.slice("file://".length);
  }

  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    decoded = value;
  }

  const withoutDrivePrefixSlash = decoded.replace(/^\/([a-zA-Z]:[\\/])/u, "$1");
  const normalized = withoutDrivePrefixSlash.replace(/\\/g, "/");
  const windowsMatch = normalized.match(/^([a-zA-Z]:)(?:\/(.*))?$/u);
  if (windowsMatch) {
    const drive = windowsMatch[1].toUpperCase();
    const rest = windowsMatch[2] ?? "";
    const parts = rest.split("/").filter(Boolean);
    if (parts.length > 0) {
      parts.pop();
    }
    return {
      drive,
      path: normalizeRelativePath(parts.join("/"))
    };
  }

  if (normalized.startsWith("/")) {
    return { drive: "", path: "" };
  }

  const parts = normalized.split("/").filter(Boolean);
  if (parts.length > 0) {
    parts.pop();
  }
  return {
    drive: "",
    path: normalizeRelativePath(parts.join("/"))
  };
};

const readStoredHostMediaLocation = (): { drive: string; path: string } => {
  if (typeof window === "undefined") {
    return { drive: "", path: "" };
  }
  const raw = window.localStorage.getItem(HOST_MEDIA_LOCATION_KEY);
  if (!raw) {
    return { drive: "", path: "" };
  }
  try {
    const parsed = JSON.parse(raw) as { drive?: unknown; path?: unknown };
    return {
      drive: typeof parsed.drive === "string" ? parsed.drive.trim().toUpperCase() : "",
      path: typeof parsed.path === "string" ? normalizeRelativePath(parsed.path) : ""
    };
  } catch {
    return { drive: "", path: "" };
  }
};

const formatFileSize = (size?: number): string => {
  if (!Number.isFinite(size) || (size ?? 0) < 0) {
    return "-";
  }
  const value = size as number;
  if (value < 1024) {
    return `${value} B`;
  }
  const units = ["KB", "MB", "GB", "TB"];
  let next = value / 1024;
  let unitIndex = 0;
  while (next >= 1024 && unitIndex < units.length - 1) {
    next /= 1024;
    unitIndex += 1;
  }
  return `${next.toFixed(next >= 10 ? 0 : 1)} ${units[unitIndex]}`;
};

const formatModifiedTime = (value?: string): string => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return date.toLocaleString();
};

const HostMediaFolder = ({ open, onClose, onSelect, authorizedFetch, initialSelectedPath, allowedExtensions }: HostMediaFolderProps) => {
  const [entries, setEntries] = useState<HostMediaEntry[]>([]);
  const [currentPath, setCurrentPath] = useState("");
  const [drives, setDrives] = useState<string[]>([]);
  const [selectedDrive, setSelectedDrive] = useState("");
  const [driveSelectionEnabled, setDriveSelectionEnabled] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<HostMediaEntry | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadIndeterminate, setDownloadIndeterminate] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 100 });
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const loadEntries = useCallback(
    async (path: string, drive: string) => {
      setLoading(true);
      setError(null);
      try {
        const queryParams = new URLSearchParams();
        if (path) {
          queryParams.set("path", path);
        }
        if (driveSelectionEnabled && drive) {
          queryParams.set("drive", drive);
        }
        const query = queryParams.toString();
        const response = await authorizedFetch(`/api/server/host-media${query ? `?${query}` : ""}`);
        const payload = (await response.json().catch(() => null)) as HostMediaResponse | { error?: string } | null;
        if (!response.ok) {
          if (response.status === 404 && path) {
            setSelectedEntry(null);
            setCurrentPath("");
            return;
          }
          const message =
            payload && "error" in payload && typeof payload.error === "string"
              ? payload.error
              : "Unable to load host media";
          throw new Error(message);
        }
        if (!payload || !("entries" in payload)) {
          throw new Error("Invalid response from host media");
        }
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        setEntries(payload.entries ?? []);
      } catch (err) {
        setEntries([]);
        setError(err instanceof Error ? err.message : "Unable to load host media");
      } finally {
        setLoading(false);
      }
    },
    [authorizedFetch, driveSelectionEnabled]
  );

  useEffect(() => {
    if (!open) {
      setInitialized(false);
      return;
    }
    setEntries([]);
    setError(null);
    setUploadError(null);
    setUploadProgress(0);
    setUploading(false);
    setActionError(null);
    setSelectedEntry(null);
    setConfirmDeleteOpen(false);
    setDownloading(false);
    setDownloadProgress(0);
    setDownloadIndeterminate(false);
    setDeleting(false);
    setCreateFolderOpen(false);
    setNewFolderName("");
    setCreatingFolder(false);
    setPaginationModel({ page: 0, pageSize: 100 });
    setEntries([]);
    setCurrentPath("");
    setDrives([]);
    setSelectedDrive("");
    setDriveSelectionEnabled(false);
    setInitialized(false);
    setInitializing(true);

    const initialLocation = parseHostMediaLocation(initialSelectedPath);
    const storedLocation = readStoredHostMediaLocation();

    let cancelled = false;
    const initLocation = async () => {
      try {
        const response = await authorizedFetch("/api/server/host-media/drives");
        const payload = (await response.json().catch(() => null)) as HostMediaDrivesResponse | null;
        if (!response.ok || !payload) {
          throw new Error("Unable to load host media drives");
        }

        const enabled = Boolean(payload.enabled);
        const nextDrives = (payload.drives ?? [])
          .filter((item): item is string => typeof item === "string" && item.trim().length > 0)
          .map((item) => item.trim().toUpperCase());
        const defaultDrive = typeof payload.selected === "string" ? payload.selected.trim().toUpperCase() : "";

        const preferredDrive = [initialLocation.drive, storedLocation.drive, defaultDrive].find(
          (candidate) => candidate && (!enabled || nextDrives.includes(candidate))
        );

        if (cancelled) {
          return;
        }

        setDriveSelectionEnabled(enabled);
        setDrives(nextDrives);
        setSelectedDrive(enabled ? preferredDrive ?? nextDrives[0] ?? "" : "");
        setCurrentPath(initialLocation.path || storedLocation.path || "");
      } catch {
        if (cancelled) {
          return;
        }
        setDriveSelectionEnabled(false);
        setDrives([]);
        setSelectedDrive("");
        setCurrentPath(initialLocation.path || storedLocation.path || "");
      } finally {
        if (!cancelled) {
          setInitializing(false);
          setInitialized(true);
        }
      }
    };

    void initLocation();
    return () => {
      cancelled = true;
    };
  }, [open, initialSelectedPath, authorizedFetch]);

  useEffect(() => {
    if (!open || !initialized) {
      return;
    }
    void loadEntries(currentPath, selectedDrive);
  }, [open, currentPath, selectedDrive, initialized, loadEntries]);

  useEffect(() => {
    if (!selectedEntry) {
      return;
    }
    const stillAvailable = entries.some((entry) => entry.relPath === selectedEntry.relPath);
    if (!stillAvailable) {
      setSelectedEntry(null);
    }
  }, [entries, selectedEntry]);

  useEffect(() => {
    if (!open || typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(
      HOST_MEDIA_LOCATION_KEY,
      JSON.stringify({
        drive: driveSelectionEnabled ? selectedDrive : "",
        path: currentPath
      })
    );
  }, [open, driveSelectionEnabled, selectedDrive, currentPath]);

  const displayPath = useMemo(() => {
    const suffix = currentPath ? `/${currentPath}` : "/";
    if (driveSelectionEnabled && selectedDrive) {
      return `${selectedDrive}${suffix}`;
    }
    return suffix;
  }, [currentPath, driveSelectionEnabled, selectedDrive]);
  const canGoUp = Boolean(currentPath);
  const normalizedAllowedExtensions = useMemo(
    () => (allowedExtensions ?? []).map((extension) => extension.toLowerCase()),
    [allowedExtensions]
  );
  const isSelectableFile = useCallback(
    (entry: HostMediaEntry) =>
      !entry.isDir &&
      (normalizedAllowedExtensions.length === 0 ||
        normalizedAllowedExtensions.some((extension) => entry.name.toLowerCase().endsWith(extension))),
    [normalizedAllowedExtensions]
  );
  const rows = useMemo<HostMediaGridRow[]>(
    () =>
      entries.filter((entry) => entry.isDir || isSelectableFile(entry)).map((entry) => ({
        ...entry,
        id: entry.relPath
      })),
    [entries, isSelectableFile]
  );
  const selectedRowModel = useMemo<GridRowSelectionModel>(
    () => ({
      type: "include",
      ids: selectedEntry ? new Set<GridRowId>([selectedEntry.relPath]) : new Set<GridRowId>()
    }),
    [selectedEntry]
  );
  const columns = useMemo<GridColDef<HostMediaGridRow>[]>(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1.2,
        minWidth: 220,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, height: "100%" }}>
            {row.isDir ? <FolderOpenIcon fontSize="small" /> : <DescriptionIcon fontSize="small" />}
            <Typography variant="body2" noWrap fontWeight={row.isDir ? 700 : 400}>
              {row.name}
            </Typography>
          </Stack>
        )
      },
      {
        field: "size",
        headerName: "Size",
        width: 110,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value, row) => (row.isDir ? "-" : formatFileSize(value as number | undefined))
      },
      {
        field: "modTime",
        headerName: "Modified",
        flex: 0.8,
        minWidth: 180,
        valueFormatter: (value) => formatModifiedTime(value as string | undefined)
      }
    ],
    []
  );

  const uploadToCurrentFolder = useCallback(
    async (file: File) => {
      setUploadError(null);
      setUploadProgress(0);
      setUploading(true);

      try {
        await new Promise<void>((resolve, reject) => {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("path", currentPath);
          if (driveSelectionEnabled && selectedDrive) {
            formData.append("drive", selectedDrive);
          }

          const xhr = new XMLHttpRequest();
          xhr.open("POST", resolveApiPath("/api/server/host-media/upload"));

          const accessToken = getAccessToken();
          if (accessToken) {
            xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
          }

          xhr.upload.onprogress = (event) => {
            if (!event.lengthComputable) {
              return;
            }
            const nextProgress = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(Math.min(100, Math.max(0, nextProgress)));
          };

          xhr.onerror = () => {
            reject(new Error("Upload failed due to a network error"));
          };

          xhr.onload = () => {
            const payload = (() => {
              try {
                return JSON.parse(xhr.responseText) as { error?: string } | null;
              } catch {
                return null;
              }
            })();

            if (xhr.status >= 200 && xhr.status < 300) {
              setUploadProgress(100);
              resolve();
              return;
            }

            const message =
              payload && typeof payload.error === "string" && payload.error.trim()
                ? payload.error
                : "Unable to upload file";
            reject(new Error(message));
          };

          xhr.send(formData);
        });

        await loadEntries(currentPath, selectedDrive);
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : "Unable to upload file");
      } finally {
        setUploading(false);
      }
    },
    [currentPath, loadEntries, driveSelectionEnabled, selectedDrive]
  );

  const handleUploadInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    void uploadToCurrentFolder(file);
  };

  const handleDownloadSelected = useCallback(async () => {
    if (!selectedEntry) {
      return;
    }
    setActionError(null);
    setDownloading(true);
    setDownloadProgress(0);
    setDownloadIndeterminate(false);
    try {
      const queryParams = new URLSearchParams();
      queryParams.set("path", selectedEntry.relPath);
      if (driveSelectionEnabled && selectedDrive) {
        queryParams.set("drive", selectedDrive);
      }
      const response = await authorizedFetch(`/api/server/host-media/download?${queryParams.toString()}`);
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(payload?.error ?? "Unable to download file");
      }
      const contentLengthHeader = response.headers.get("content-length");
      const totalSize = contentLengthHeader ? Number.parseInt(contentLengthHeader, 10) : NaN;
      if (!Number.isFinite(totalSize) || totalSize <= 0 || !response.body) {
        setDownloadIndeterminate(true);
        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = selectedEntry.name;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
        return;
      }

      const reader = response.body.getReader();
      const chunks: BlobPart[] = [];
      let received = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        if (value) {
          // Clone the chunk into a plain Uint8Array to satisfy BlobPart typing.
          chunks.push(Uint8Array.from(value));
          received += value.length;
          const nextProgress = Math.round((received / totalSize) * 100);
          setDownloadProgress(Math.min(100, Math.max(0, nextProgress)));
        }
      }

      const blob = new Blob(chunks);
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = selectedEntry.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Unable to download file");
    } finally {
      setDownloading(false);
      setDownloadIndeterminate(false);
    }
  }, [authorizedFetch, selectedEntry, driveSelectionEnabled, selectedDrive]);

  const handleDeleteSelected = useCallback(async () => {
    if (!selectedEntry) {
      return;
    }
    setActionError(null);
    setDeleting(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.set("path", selectedEntry.relPath);
      if (driveSelectionEnabled && selectedDrive) {
        queryParams.set("drive", selectedDrive);
      }
      const response = await authorizedFetch(`/api/server/host-media/delete?${queryParams.toString()}`, {
        method: "DELETE"
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(payload?.error ?? "Unable to delete selected path");
      }
      setSelectedEntry(null);
      await loadEntries(currentPath, selectedDrive);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Unable to delete selected path");
    } finally {
      setDeleting(false);
      setConfirmDeleteOpen(false);
    }
  }, [authorizedFetch, selectedEntry, loadEntries, currentPath, driveSelectionEnabled, selectedDrive]);

  const handleCreateFolder = useCallback(async () => {
    const trimmedName = newFolderName.trim();
    if (!trimmedName) {
      setActionError("Folder name is required");
      return;
    }

    setActionError(null);
    setCreatingFolder(true);
    try {
      const response = await authorizedFetch("/api/server/host-media/folder", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          path: currentPath,
          name: trimmedName,
          drive: driveSelectionEnabled ? selectedDrive : ""
        })
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(payload?.error ?? "Unable to create folder");
      }
      setCreateFolderOpen(false);
      setNewFolderName("");
      await loadEntries(currentPath, selectedDrive);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Unable to create folder");
    } finally {
      setCreatingFolder(false);
    }
  }, [authorizedFetch, currentPath, loadEntries, newFolderName, driveSelectionEnabled, selectedDrive]);

  const isHostMediaLoading = initializing || loading;
  const actionDisabled = uploading || isHostMediaLoading || downloading || deleting || creatingFolder;
  const fileSelected = Boolean(selectedEntry && isSelectableFile(selectedEntry));
  const entrySelected = Boolean(selectedEntry);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
      <Box display="inline-flex" alignItems="center" gap={1}>
        <FolderOpenIcon sx={{ color: "text.secondary" }} />
          Host media
        </Box>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          overflow: "hidden"
        }}
      >
        <Stack spacing={2} sx={{ minHeight: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              {driveSelectionEnabled ? (
                <FormControl size="small" sx={{ minWidth: 110 }}>
                  <InputLabel id="host-media-drive-label">Drive</InputLabel>
                  <Select
                    labelId="host-media-drive-label"
                    label="Drive"
                    value={selectedDrive}
                    onChange={(event) => {
                      const nextDrive = String(event.target.value ?? "").toUpperCase();
                      setSelectedEntry(null);
                      setSelectedDrive(nextDrive);
                      setCurrentPath("");
                    }}
                    disabled={isHostMediaLoading || drives.length === 0}
                  >
                    {drives.map((drive) => (
                      <MenuItem key={drive} value={drive}>
                        {drive}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
              <Typography variant="body2" color="text.secondary">
                Path: {displayPath}
              </Typography>
            </Stack>
            <Tooltip title="Go to parent folder" placement="top" arrow>
              <span>
                <IconButton
                  aria-label="Go to parent folder"
                  size="small"
                  disabled={!canGoUp || isHostMediaLoading}
                  onClick={() => {
                    setCurrentPath((prev) => getParentPath(prev));
                  }}
                >
                  <ArrowCircleUpIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {isHostMediaLoading ? (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                {initializing ? "Loading host media locations..." : "Loading host media content..."}
              </Typography>
              <LinearProgress />
            </Box>
          ) : null}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)"),
              minHeight: 240,
              overflow: "hidden"
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              loading={isHostMediaLoading}
              density="compact"
              rowHeight={34}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={HOST_MEDIA_PAGE_SIZE_OPTIONS}
              hideFooter={rows.length <= paginationModel.pageSize}
              disableColumnMenu
              disableColumnResize
              disableMultipleRowSelection
              disableRowSelectionOnClick={false}
              rowSelectionModel={selectedRowModel}
              onRowClick={(params) => {
                setSelectedEntry(params.row);
              }}
              onRowDoubleClick={(params) => {
                if (params.row.isDir) {
                  setSelectedEntry(null);
                  setCurrentPath(params.row.relPath);
                  return;
                }
                if (isSelectableFile(params.row)) {
                  onSelect({ fullPath: params.row.fullPath, relPath: params.row.relPath, drive: selectedDrive });
                }
              }}
              onRowSelectionModelChange={(nextSelection) => {
                if (Array.isArray(nextSelection)) {
                  const selectedId = nextSelection.length > 0 ? String(nextSelection[0]) : "";
                  const nextEntry = entries.find((entry) => entry.relPath === selectedId) ?? null;
                  setSelectedEntry(nextEntry);
                  return;
                }
                if (nextSelection && typeof nextSelection === "object" && "ids" in nextSelection) {
                  const ids = normalizeSelectionIds((nextSelection as { ids?: Set<unknown> | Iterable<unknown> }).ids);
                  const [selectedId] = Array.from(ids);
                  const nextEntry = selectedId
                    ? (entries.find((entry) => entry.relPath === String(selectedId)) ?? null)
                    : null;
                  setSelectedEntry(nextEntry);
                  return;
                }
                setSelectedEntry(null);
              }}
              sx={{
                border: "none",
                "& .MuiDataGrid-row": {
                  cursor: "pointer"
                },
                "& .MuiDataGrid-overlayWrapperInner": {
                  minHeight: 200
                }
              }}
              slotProps={{
                loadingOverlay: { variant: "circular-progress", noRowsVariant: "circular-progress" },
                noRowsOverlay: {
                  children: (
                    <Typography variant="body2" color="text.secondary">
                      No matching files found.
                    </Typography>
                  )
                }
              }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Select a host media file to use it as the stream source.
          </Typography>
        </Stack>
      </DialogContent>
      <Box sx={{ px: 3, pt: 1 }}>
        {uploading || downloading ? (
          <LinearProgress
            variant={downloadIndeterminate ? "indeterminate" : "determinate"}
            value={
              downloading
                ? downloadProgress
                : uploadProgress
            }
          />
        ) : null}
        {uploadError ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {uploadError}
          </Alert>
        ) : null}
        {actionError ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {actionError}
          </Alert>
        ) : null}
      </Box>
      <DialogActions sx={{ justifyContent: "space-between", width: "100%" }}>
        <input
          ref={fileInputRef}
          type="file"
          accept={normalizedAllowedExtensions.join(",") || undefined}
          hidden
          onChange={handleUploadInputChange}
        />
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="Create folder" placement="top" arrow>
            <span>
              <IconButton
                aria-label="Create folder"
                size="small"
                disabled={actionDisabled}
                onClick={() => {
                  setActionError(null);
                  setCreateFolderOpen(true);
                }}
              >
                <CreateNewFolderIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Upload file" placement="top" arrow>
            <span>
              <IconButton
                aria-label="Upload file"
                size="small"
                disabled={actionDisabled}
                onClick={() => {
                  setUploadError(null);
                  fileInputRef.current?.click();
                }}
              >
                <DriveFolderUploadIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Download file" placement="top" arrow>
            <span>
              <IconButton
                aria-label="Download file"
                size="small"
                disabled={!fileSelected || actionDisabled}
                onClick={() => {
                  void handleDownloadSelected();
                }}
              >
                <FileDownloadIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete selected path" placement="top" arrow>
            <span>
              <IconButton
                aria-label="Delete selected path"
                size="small"
                disabled={!entrySelected || actionDisabled}
                onClick={() => setConfirmDeleteOpen(true)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {fileSelected ? (
            <Button
              onClick={() => {
                if (selectedEntry) {
                  onSelect({ fullPath: selectedEntry.fullPath, relPath: selectedEntry.relPath, drive: selectedDrive });
                }
              }}
              disabled={actionDisabled}
            >
              Select
            </Button>
          ) : null}
          <Button onClick={onClose} disabled={actionDisabled}>Cancel</Button>
        </Stack>
      </DialogActions>
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        aria-labelledby="host-media-delete-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="host-media-delete-title">
          {selectedEntry?.isDir ? "Delete host media folder?" : "Delete host media file?"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            {selectedEntry ? `Delete "${selectedEntry.name}" from ${displayPath}?` : "No item selected."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleDeleteSelected();
            }}
            color="error"
            disabled={!selectedEntry || deleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={createFolderOpen}
        onClose={() => {
          if (!creatingFolder) {
            setCreateFolderOpen(false);
          }
        }}
        aria-labelledby="host-media-create-folder-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="host-media-create-folder-title">Create folder</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Create folder under {displayPath}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="Folder name"
              size="small"
              value={newFolderName}
              onChange={(event) => setNewFolderName(event.target.value)}
              disabled={creatingFolder}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void handleCreateFolder();
                }
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateFolderOpen(false);
            }}
            disabled={creatingFolder}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleCreateFolder();
            }}
            disabled={creatingFolder}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default HostMediaFolder;
